import type {
	AlertBannerCardProps,
	AlertType,
} from '/types/alerts.d';

import { useEffect, useState } from 'react';

import { forceArray } from '/lib/helsecore/forceArray';

import {
	ACCEPT_APPLICATION_JSON,
	CONTENT_TYPE_APPLICATION_JSON_UTF8,
} from '../../../services/formInput/constants';
import { addBulletPoints } from '/react4xp/overdosefare/alerts/addBulletPoints';

import { alertsQueryDslConnection } from './alertsQueryDslConnection';
import { calculateActivePage } from './calculateActivePage';
import { cursorToNumber } from './cursorToNumber';
import { ucFirst } from './ucFirst';
import { numberToCursor } from './numberToCursor';
import { humanizeCountyBuckets } from './humanizeCountyBuckets';
import { useUpdateEffect } from '/react4xp/shared/Lightbox/useUpdateEffect';


const TRACE = false;
const MIN_DURATION = 400;

export const ITEMS_PER_PAGE = 10;


export interface Bucket {
	docCount: number;
	key: string;
}


interface Buckets {
	buckets: Bucket[]
}


interface AlertsResult {
	data: {
		guillotine: {
			counties: {
				aggregationsAsJson: {
					county: Buckets;
				},
			}
			drugTypes: {
				aggregationsAsJson: {
					drugType: Buckets;
				},
			}
			alerts: {
				edges: {
					node: {
						_id: string,
						data: {
							appearance: string;
							description: {
								raw: string;
							};
							drugForm: string;
							drugType: string;
							drugTypeSpecific?: string;
							expires?: string;
							image: {
								data: {
									altText: string | null;
									caption: string | null;
								}
								mediaUrl: string;
								w1920: string;
								x: {
									media: {
										imageInfo: {
											imageWidth: string;
										}
									}
								}
							}[];
							isDrugAnalyzed: boolean;
							localizedAreaOrCityAndCounty?: string | null;
							locationAreaOrCity: string;
							locationCounty: string;
							modified?: string;
							published: string;
							recommendation: {
								raw: string;
							};
							title: string;
							alertType: AlertType;
						}
					}
				}[];
				pageInfo: {
					endCursor: string;
					// hasNext: boolean;
					startCursor: string;
				}
				totalCount: number;
			}
		}
	}
}


interface ExpiredAlertsState {
	activePage: number;
	countyFilterList: Bucket[] | null;
	countyOnChange: (county: string) => void;
	countyValue: Bucket | null;
	currentItems: AlertBannerCardProps[];
	drugTypeOnChange: (drugType: string) => void;
	drugTypesFilterList: Bucket[] | null;
	drugValue: Bucket | null;
	emptyFilters: () => void;
	filteredExpiredAlertsCount: number;
	onPageClick: (zeroBasedPage: number) => void;
	showSpinner: boolean;
}


export function useExpiredAlertsState({
	countyBuckets,
	drugTypeBuckets,
	expiredAlerts,
	expiredAlertsCount,
	graphqlServiceUrl,
	locale,
	initialCountyFromParams,
	initialDrugTypeFromParams,
}: {
	countyBuckets: Bucket[];
	drugTypeBuckets: Bucket[];
	expiredAlerts: AlertBannerCardProps[];
	expiredAlertsCount: number;
	graphqlServiceUrl: string;
	locale: string;
	initialCountyFromParams?: string;
	initialDrugTypeFromParams?: string;
}): ExpiredAlertsState {
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// State
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	const [startedFetchingAlertsAt, setStartedFetchingAlertsAt] = useState<null|number>(null);
	const [showSpinner, setShowSpinner] = useState(false);
	const [countyFilterList, setCountyFilterList] = useState<Bucket[]>(countyBuckets);
	const [drugTypesFilterList, setDrugTypesFilterList] = useState<Bucket[]>(drugTypeBuckets);

	const [filteredExpiredAlertsCount, setFilteredExpiredAlertsCount] = useState(expiredAlertsCount);

	const [countyValue, setCountyValue] = useState(
		initialCountyFromParams
			? countyBuckets.find(b => b.key === initialCountyFromParams) || null
			: null,
	);

	const [drugValue, setDrugValue] = useState(
		initialDrugTypeFromParams
			? drugTypeBuckets.find(b => b.key === initialDrugTypeFromParams) || null
			: null,
	);

	const [activePage, setActivePage] = useState(0);

	const [currentItems, setCurrentItems] = useState<AlertBannerCardProps[]>(expiredAlerts);

	const updateUrlParam = (key: string, value: string | null): void => {
		const url = new URL(window.location.href);

		if (value) {
			url.searchParams.set(key, value);
		} else {
			url.searchParams.delete(key);
		}

		window.history.replaceState({}, "", url.toString());
	};

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// Functions
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	function fetchAlerts({
		after,
		county,
		drugType,
	}: {
		after? : string | null;
		county?: string | null;
		drugType?: string | null;
	}): void {
		setShowSpinner(true);
		setStartedFetchingAlertsAt(Date.now());
		const now = new Date().toISOString();
		void fetch(graphqlServiceUrl, {
			body: JSON.stringify({
				query: alertsQueryDslConnection({
					after,
					first: ITEMS_PER_PAGE,
					county,
					drugType,
					locale,
					now,
				}),
			}),
			headers: {
				'Accept': ACCEPT_APPLICATION_JSON,
				'Content-Type': CONTENT_TYPE_APPLICATION_JSON_UTF8,
			},
			method: 'POST',
		}).then(r => r.json() as Promise<AlertsResult>).then(data => {
			if (TRACE) console.debug(data);
			const {
				aggregationsAsJson: {
					county: {
						buckets: countyBuckets,
					},
				},
			} = data.data.guillotine.counties;
			const humanizedCountyBuckets = humanizeCountyBuckets(countyBuckets);
			setCountyFilterList(humanizedCountyBuckets);
			const countyBucket = county && humanizedCountyBuckets?.filter(({key}) => key === county)[0] || null;
			setCountyValue(countyBucket);

			const {
				aggregationsAsJson: {
					drugType: {
						buckets: drugTypeBuckets,
					},
				},
			} = data.data.guillotine.drugTypes;
			setDrugTypesFilterList(drugTypeBuckets);
			const drugBucket = drugType && drugTypeBuckets?.filter(({key}) => key === drugType)[0] || null;
			setDrugValue(drugBucket);

			const {
				edges,
				pageInfo: {
					endCursor,
					startCursor,
				},
				totalCount,
			} = data.data.guillotine.alerts;

			const endIndex = cursorToNumber(endCursor) - 1;
			const startIndex = cursorToNumber(startCursor) - 1;
			if (TRACE) console.debug(
				'startCursor', startCursor,
				'startIndex', startIndex,
				'endCursor', endCursor,
				'endIndex', endIndex,
			);

			const alertBannerCardPropsArray: AlertBannerCardProps[] = [];
			for (let i = 0; i < edges.length; i++) {
				const {
					_id: id,
					data,
				} = edges[i].node;
				const {
					appearance,
					description: {
						raw: description,
					},
					drugForm,
					drugType,
					drugTypeSpecific,
					expires,
					image,
					isDrugAnalyzed,
					localizedAreaOrCityAndCounty,
					locationAreaOrCity,
					locationCounty,
					modified,
					published,
					recommendation: {
						raw: recommendation,
					},
					title,
					alertType,
				} = data;
				const imageArray = forceArray(image);
				const firstImage = imageArray.length ? imageArray[0] : null;
				alertBannerCardPropsArray.push({
					content: {
						appearance, // This in only a single line in Sakbehandlingsløsningen.
						description: addBulletPoints(description),
						drugForm,
						drugType,
						drugTypeSpecific,
						isDrugAnalyzed,
						recommendation: addBulletPoints(recommendation),
					},
					date: {
						expireStringOrUndef: expires,
						isExpired: expires ? new Date(expires) < new Date(now) : false,
						isModified: !!modified,
						modified,
						published,
					} as AlertBannerCardProps['date'],
					id,
					image: firstImage
						? {
							url: (
								// vector graphics doesn't have imageWidth
								firstImage?.x?.media?.imageInfo?.imageWidth
								&& parseInt(firstImage.x.media.imageInfo.imageWidth) > 1920
							)
								? firstImage.w1920 // Scale down
								: firstImage.mediaUrl, // Use full unscaled image
							alt: firstImage.data.altText || undefined,
							caption: firstImage.data.caption || undefined,
						}
						: null,
					location: (locationAreaOrCity && locationCounty)
						? localizedAreaOrCityAndCounty as string
						: locationAreaOrCity
							? ucFirst(locationAreaOrCity)
							: locationCounty || '',
					title,
					variant: 'accordion',
					alertType,
				}); // push
			} // for
			const newOneBasedActivePage = calculateActivePage({
				endIndex,
				itemsPerPage: ITEMS_PER_PAGE,
				startIndex,
			});
			const newZeroBasedActivePage = newOneBasedActivePage - 1;
			if (TRACE) console.debug(
				'fetchAlerts ',
				'newOneBasedActivePage', newOneBasedActivePage,
				'newZeroBasedActivePage', newZeroBasedActivePage,
			);
			setActivePage(newZeroBasedActivePage); // FRISK Paginator activePage is 0-based
			setCurrentItems(alertBannerCardPropsArray);
			setFilteredExpiredAlertsCount(totalCount);
		}).catch((error) => {
			console.error('fetch failed:', error);
			setActivePage(0);
			setCurrentItems([]);
			setFilteredExpiredAlertsCount(0);
		});
	}

	function countyOnChange(county: string): void {
		updateUrlParam("county", county);
		fetchAlerts({
			after: null,
			county,
			drugType: drugValue?.key ?? null,
		});
	}

	function drugTypeOnChange(drugType: string): void {
		updateUrlParam("drugType", drugType);
		fetchAlerts({
			after: null,
			county: countyValue?.key ?? null,
			drugType,
		});
	}

	const emptyFilters = (): void => {
		updateUrlParam("county", null);
		updateUrlParam("drugType", null);

		fetchAlerts({
			after: null,
			county: null,
			drugType: null,
		});
	};

	// Clicking on (1) gives zeroBasedPage=0 and oneBasedPage=1 and should give oneBasedAfter of 0
	// Clicking on (2) gives zeroBasedPage=1 and oneBasedPage=2 and should give oneBasedAfter of 10
	const onPageClick = (zeroBasedPage: number): void => {
		const oneBasedPage = zeroBasedPage + 1;

		// (1 * 10) - 10 = 0
		// (2 * 10) - 10 = 10
		const oneBasedAfter = (oneBasedPage * ITEMS_PER_PAGE) - ITEMS_PER_PAGE;
		const after = numberToCursor(oneBasedAfter);

		fetchAlerts({
			after,
			county: countyValue?.key,
			drugType: drugValue?.key,
		});
	};

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// Effects
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	useUpdateEffect(() => {
		if (startedFetchingAlertsAt) {
			const elapsed = Date.now() - startedFetchingAlertsAt;
			const remaining = MIN_DURATION - elapsed;
			setTimeout(() => {
				setShowSpinner(false);
			}, Math.max(0, remaining));
		}
	}, [currentItems]);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const url = new URL(window.location.href);

		// ----------------------------------------------------------------------
		// 1. Apply ?expired=true on mount to open expired tab
		// ----------------------------------------------------------------------
		url.searchParams.set("expired", "true");

		// ----------------------------------------------------------------------
		// 2. If controller gave initial params, rewrite URL to reflect them
		// ----------------------------------------------------------------------
		if (countyValue) {
			url.searchParams.set("county", countyValue.key);
		}
		if (drugValue) {
			url.searchParams.set("drugType", drugValue.key);
		}

		window.history.replaceState({}, "", url.toString());

		// ----------------------------------------------------------------------
		// 3. FIRE INITIAL FILTER FETCH (only once)
		// ----------------------------------------------------------------------
		if (countyValue || drugValue) {
			fetchAlerts({
				after: null,
				county: countyValue ? countyValue.key : null,
				drugType: drugValue ? drugValue.key : null,
			});
		}

		// ----------------------------------------------------------------------
		// 4. Cleanup on unmount: remove params but DO NOT refetch
		// ----------------------------------------------------------------------
		return () => {
			const clean = new URL(window.location.href);
			clean.searchParams.delete("expired");
			clean.searchParams.delete("county");
			clean.searchParams.delete("drugType");
			window.history.replaceState({}, "", clean.toString());
		};
	}, []); // runs once

	return {
		activePage,
		countyFilterList,
		countyOnChange,
		countyValue,
		currentItems,
		drugTypeOnChange,
		drugTypesFilterList,
		drugValue,
		emptyFilters,
		filteredExpiredAlertsCount,
		onPageClick,
		showSpinner,
	};
}
